import React from 'react'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { makeStyles, Button } from '@paintscout/ui'
import type { DropzoneState } from 'react-dropzone'

import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import { alpha } from '@material-ui/core/styles/colorManipulator'

import CoreDropzone from '../../components/Dropzone'
import type { DropzoneProps } from '../../components/Dropzone'

/**
 * The presentation editor's variant of {@link CoreDropzone}. It renders the same
 * bordered "Add" box it always has, but is now a thin wrapper over the up-to-date
 * core Dropzone so it inherits the iPad/iOS photo-picker "lift" fix (#20634), the
 * window drag-block, the Chrome empty-`type` reject guard, and `DEFAULT_FILE_ACCEPT`
 * for free. Kept as a separate component so presentation styling stays isolated from
 * the core (per #21305). v2 adds no new props, so `DropzoneProps` is re-exported as-is.
 */
export type { DropzoneProps }

const useStyles = makeStyles((theme: Theme) => ({
  addBody: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.palette.grey[500]}`,
    height: '100%',
    '&:hover': {
      cursor: 'pointer',
      borderColor: theme.palette.primary.main,
      color: alpha(theme.palette.primary.main, 0.8)
    }
  },
  addWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: `${theme.spacing(3)}px 0`
  },
  addText: {
    paddingTop: theme.spacing(2)
  }
}))

function PresentationDropzone({ shouldRenderPreview, renderPreview, text, ...rest }: Omit<DropzoneProps, 'classes'>) {
  const classes = useStyles()

  const renderAddBox = (args: DropzoneState) => {
    // Mirror the core's chrome-safe reject check (chrome doesn't populate dragged file types).
    const rejectDrag = args.isDragReject && args.draggedFiles.filter((file) => file.type !== '').length > 0
    const message = rejectDrag ? 'Invalid File Type' : text || 'Add'
    return (
      <div className={classes.addBody}>
        <div className={classes.addWrapper}>
          <ArrowUpwardIcon />
          <Button className={classes.addText} variant="outlined" color="primary">
            {message}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <CoreDropzone
      {...rest}
      text={text}
      shouldRenderPreview={true}
      renderPreview={(args) => (shouldRenderPreview && renderPreview ? renderPreview(args) : renderAddBox(args))}
    />
  )
}

export default PresentationDropzone
